import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import Icon from 'components/AppIcon';


const NAV_ITEMS = [
  {
    label: 'Create',
    path: '/creator-dashboard',
    icon: 'Wand2',
    tooltip: 'AI Game Creator',
  },
  {
    label: 'Play',
    path: '/home-landing-screen',
    icon: 'Gamepad2',
    tooltip: 'Discover & Play Games',
    altPaths: ['/game-player-screen'],
  },
  {
    label: 'Library',
    path: '/library',
    icon: 'Library',
    tooltip: 'My Creations Library',
  },
  {
    label: 'Discover',
    path: '/discover',
    icon: 'Compass',
    tooltip: 'Community & Trending',
  },
  {
    label: 'Marketplace',
    path: '/marketplace',
    icon: 'ShoppingBag',
    tooltip: 'AI Asset Marketplace',
  },
  {
    label: 'Pricing',
    path: '/pricing',
    icon: 'CreditCard',
    tooltip: 'Plans & Pricing',
  },
  {
    label: 'Account',
    path: '/account',
    icon: 'User',
    tooltip: 'Profile & Settings',
  },
];

const USER_MENU_ITEMS = [
  { label: 'My Profile', icon: 'User', path: '/account' },
  { label: 'My Games', icon: 'Library', path: '/creator-dashboard' },
  { label: 'Plans & Pricing', icon: 'CreditCard', path: '/pricing' },
  { label: 'Safety Settings', icon: 'Shield', path: '/account/safety' },
  { label: 'Preferences', icon: 'Settings', path: '/account/preferences' },
  { label: 'Help & Support', icon: 'HelpCircle', path: '/help' },
  { divider: true },
  { label: 'Sign Out', icon: 'LogOut', danger: true, action: 'signout' },
];

export default function PrimaryTabNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const userMenuRef = useRef(null);

  const isActive = (item) => {
    if (location?.pathname === item?.path) return true;
    if (item?.altPaths) {
      return item?.altPaths?.some((p) => location?.pathname?.startsWith(p));
    }
    return false;
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(e?.target)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const next = !prev;
      document.documentElement?.classList?.toggle('dark', next);
      return next;
    });
  };

  const handleUserMenuAction = (item) => {
    setUserMenuOpen(false);
    if (item?.action === 'signout') {
      navigate('/home-landing-screen');
      return;
    }
    if (item?.path) navigate(item?.path);
  };

  const handleNavKeyDown = (e, path) => {
    if (e?.key === 'Enter' || e?.key === ' ') {
      e?.preventDefault();
      navigate(path);
    }
  };

  return (
    <>
      {/* Mobile top bar - logo only */}
      <div className="mobile-top-bar" role="banner">
        <Link to="/home-landing-screen" className="tab-nav-logo" aria-label="GameForge Home">
          <div className="tab-nav-logo-icon">
            <Icon name="Zap" size={18} color="white" strokeWidth={2.5} />
          </div>
          <span className="tab-nav-logo-text">GameForge</span>
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleDarkMode}
            className="flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-all duration-250"
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <Icon name={darkMode ? 'Sun' : 'Moon'} size={18} />
          </button>
          <div className="relative" ref={null}>
            <button
              className="user-menu-trigger"
              onClick={() => setUserMenuOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={userMenuOpen}
              aria-label="User menu"
            >
              <div className="user-avatar">
                <span>JD</span>
              </div>
            </button>
          </div>
        </div>
      </div>
      {/* Primary tab navigation */}
      <nav className="tab-nav" role="navigation" aria-label="Primary navigation">
        <div className="tab-nav-inner">
          {/* Logo - desktop only */}
          <Link to="/home-landing-screen" className="tab-nav-logo" aria-label="GameForge Home">
            <div className="tab-nav-logo-icon">
              <Icon name="Zap" size={18} color="white" strokeWidth={2.5} />
            </div>
            <span className="tab-nav-logo-text">GameForge</span>
          </Link>

          {/* Nav items */}
          <div className="tab-nav-items" role="tablist" aria-label="Main sections">
            {NAV_ITEMS?.map((item) => {
              const active = isActive(item);
              return (
                <button
                  key={item?.path}
                  role="tab"
                  aria-selected={active}
                  aria-label={item?.tooltip}
                  title={item?.tooltip}
                  className={`tab-nav-item${active ? ' active' : ''}`}
                  onClick={() => navigate(item?.path)}
                  onKeyDown={(e) => handleNavKeyDown(e, item?.path)}
                  tabIndex={0}
                >
                  <Icon
                    name={item?.icon}
                    size={18}
                    color={active ? 'var(--color-primary)' : 'currentColor'}
                    strokeWidth={active ? 2.5 : 2}
                  />
                  <span>{item?.label}</span>
                </button>
              );
            })}
          </div>

          {/* Right side controls - desktop */}
          <div className="tab-nav-right">
            <button
              onClick={toggleDarkMode}
              className="flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground hover:text-primary hover:bg-muted transition-all duration-250"
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <Icon name={darkMode ? 'Sun' : 'Moon'} size={18} />
            </button>

            {/* User context menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                className="user-menu-trigger"
                onClick={() => setUserMenuOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={userMenuOpen}
                aria-label="Open user menu"
              >
                <div className="user-avatar">
                  <span>JD</span>
                </div>
                <span className="user-menu-name">Jane Doe</span>
                <Icon
                  name={userMenuOpen ? 'ChevronUp' : 'ChevronDown'}
                  size={14}
                  color="var(--color-muted-foreground)"
                />
              </button>

              {userMenuOpen && (
                <div
                  className="user-menu-dropdown"
                  role="menu"
                  aria-label="User account menu"
                >
                  <div className="user-menu-header">
                    <div className="flex items-center gap-10">
                      <div className="user-avatar" style={{ width: 40, height: 40, fontSize: '1rem' }}>
                        <span>JD</span>
                      </div>
                      <div>
                        <p
                          className="font-heading font-semibold text-sm"
                          style={{ color: 'var(--color-foreground)' }}
                        >
                          Jane Doe
                        </p>
                        <p
                          className="font-caption text-xs"
                          style={{ color: 'var(--color-muted-foreground)' }}
                        >
                          jane@example.com
                        </p>
                      </div>
                    </div>
                  </div>

                  {USER_MENU_ITEMS?.map((item, idx) => {
                    if (item?.divider) {
                      return <div key={`divider-${idx}`} className="user-menu-divider" role="separator" />;
                    }
                    return (
                      <button
                        key={item?.label}
                        role="menuitem"
                        className={`user-menu-item${item?.danger ? ' danger' : ''}`}
                        onClick={() => handleUserMenuAction(item)}
                        tabIndex={0}
                      >
                        <Icon
                          name={item?.icon}
                          size={16}
                          color={item?.danger ? 'var(--color-error)' : 'var(--color-muted-foreground)'}
                        />
                        <span>{item?.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}